
import React from 'react';
import { Subscription } from '@/types/subscription';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { User } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSubscriptionCancellation } from '@/hooks/useSubscriptionCancellation';
import { EmptySubscriptions } from './EmptySubscriptions';
import { SubscriptionRow } from './SubscriptionRow';
import { MobileSubscriptionCard } from './MobileSubscriptionCard';

interface SubscriptionManagementTableProps {
  subscriptions: Subscription[];
  onRefresh: () => void;
}

export function SubscriptionManagementTable({ 
  subscriptions, 
  onRefresh 
}: SubscriptionManagementTableProps) {
  const { cancellingId, handleCancelSubscription } = useSubscriptionCancellation();
  const isMobile = useIsMobile();

  const onCancelSubscription = (subscription: Subscription) => {
    handleCancelSubscription(subscription, onRefresh);
  };

  if (subscriptions.length === 0) {
    return <EmptySubscriptions />;
  }

  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <User className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Your Subscriptions ({subscriptions.length})</h2>
        </div>
        {subscriptions.map((subscription) => (
          <MobileSubscriptionCard
            key={subscription.id}
            subscription={subscription}
            cancellingId={cancellingId}
            onCancel={onCancelSubscription}
          />
        ))}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Your Subscriptions ({subscriptions.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Bin Day</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>Total Paid</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((subscription) => (
                <SubscriptionRow
                  key={subscription.id}
                  subscription={subscription}
                  cancellingId={cancellingId}
                  onCancel={onCancelSubscription}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
